import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import { FiSearch, FiMapPin, FiCalendar, FiUsers } from 'react-icons/fi';
import api from '../services/api';

const SearchBox = ({ variant = 'hero' }) => {
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [formData, setFormData] = useState({
    location: '',
    checkIn: null,
    checkOut: null,
    guests: 1
  });

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const data = await api.getLocations();
        setLocations(data);
      } catch (error) {
        console.error('Error fetching locations:', error);
      }
    };
    fetchLocations();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();

    if (formData.location) params.append('location', formData.location);
    if (formData.checkIn) params.append('checkIn', formData.checkIn.toISOString().split('T')[0]);
    if (formData.checkOut) params.append('checkOut', formData.checkOut.toISOString().split('T')[0]);
    if (formData.guests) params.append('guests', formData.guests);

    navigate(`/apartments?${params.toString()}`);
  };

  const isHero = variant === 'hero';

  const styles = {
    container: {
      background: 'white',
      borderRadius: isHero ? '16px' : '12px',
      padding: isHero ? '8px' : '16px',
      boxShadow: isHero ? '0 8px 28px rgba(0,0,0,0.15)' : '0 2px 8px rgba(0,0,0,0.08)',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      flexWrap: 'wrap'
    },
    field: {
      flex: isHero ? '1 1 180px' : '1',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: isHero ? '12px 20px' : '8px 12px',
      borderRight: isHero ? '1px solid #eee' : 'none',
      minWidth: '150px'
    },
    fieldLast: {
      flex: isHero ? '1 1 180px' : '1',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: isHero ? '12px 20px' : '8px 12px',
      borderRight: 'none',
      minWidth: '150px'
    },
    icon: {
      color: '#1a3a5c',
      fontSize: '1.25rem',
      flexShrink: 0
    },
    fieldContent: {
      flex: 1,
      minWidth: 0
    },
    label: {
      fontSize: '0.75rem',
      fontWeight: '600',
      color: '#222',
      marginBottom: '4px',
      display: isHero ? 'block' : 'none'
    },
    input: {
      width: '100%',
      border: 'none',
      outline: 'none',
      fontSize: '0.9375rem',
      color: '#222',
      background: 'transparent'
    },
    select: {
      width: '100%',
      border: 'none',
      outline: 'none',
      fontSize: '0.9375rem',
      color: '#222',
      background: 'transparent',
      cursor: 'pointer'
    },
    searchBtn: {
      background: '#1a3a5c',
      color: 'white',
      border: 'none',
      borderRadius: isHero ? '12px' : '8px',
      padding: isHero ? '16px 32px' : '12px 24px',
      fontSize: '1rem',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      transition: 'background 0.2s ease',
      flexShrink: 0
    },
    datePicker: {
      width: '100%',
      border: 'none',
      outline: 'none',
      fontSize: '0.9375rem',
      color: '#222',
      background: 'transparent',
      cursor: 'pointer'
    }
  };

  return (
    <form onSubmit={handleSearch} style={styles.container} className="search-box">
      {/* Location */}
      <div style={styles.field}>
        <FiMapPin style={styles.icon} />
        <div style={styles.fieldContent}>
          <label style={styles.label}>Location</label>
          <select
            style={styles.select}
            value={formData.location}
            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
          >
            <option value="">Any location</option>
            {locations.map(loc => (
              <option key={loc._id} value={loc.slug}>{loc.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Check-in */}
      <div style={styles.field}>
        <FiCalendar style={styles.icon} />
        <div style={styles.fieldContent}>
          <label style={styles.label}>Check-in</label>
          <DatePicker
            selected={formData.checkIn}
            onChange={(date) => setFormData({ ...formData, checkIn: date })}
            selectsStart
            startDate={formData.checkIn}
            endDate={formData.checkOut}
            minDate={new Date()}
            placeholderText="Add date"
            dateFormat="dd MMM yyyy"
            className="search-date-picker"
          />
        </div>
      </div>

      {/* Check-out */}
      <div style={styles.field}>
        <FiCalendar style={styles.icon} />
        <div style={styles.fieldContent}>
          <label style={styles.label}>Check-out</label>
          <DatePicker
            selected={formData.checkOut}
            onChange={(date) => setFormData({ ...formData, checkOut: date })}
            selectsEnd
            startDate={formData.checkIn}
            endDate={formData.checkOut}
            minDate={formData.checkIn || new Date()}
            placeholderText="Add date"
            dateFormat="dd MMM yyyy"
            className="search-date-picker"
          />
        </div>
      </div>

      {/* Guests */}
      <div style={styles.fieldLast}>
        <FiUsers style={styles.icon} />
        <div style={styles.fieldContent}>
          <label style={styles.label}>Guests</label>
          <select
            style={styles.select}
            value={formData.guests}
            onChange={(e) => setFormData({ ...formData, guests: parseInt(e.target.value) })}
          >
            {[1, 2, 3, 4, 5, 6, 7, 8].map(num => (
              <option key={num} value={num}>{num} guest{num > 1 ? 's' : ''}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Search Button */}
      <button type="submit" style={styles.searchBtn}>
        <FiSearch />
        {isHero && 'Search'}
      </button>

      <style>{`
        .search-date-picker {
          width: 100%;
          border: none;
          outline: none;
          font-size: 0.9375rem;
          color: #222;
          background: transparent;
          cursor: pointer;
        }
        @media (max-width: 768px) {
          .search-box {
            flex-direction: column;
            padding: 16px !important;
          }
          .search-box > div {
            width: 100%;
            border-right: none !important;
            border-bottom: 1px solid #eee;
            padding: 12px 0 !important;
          }
          .search-box > div:last-of-type {
            border-bottom: none;
          }
          .search-box button {
            width: 100%;
            margin-top: 8px;
          }
        }
      `}</style>
    </form>
  );
};

export default SearchBox;
